import React, { useState, useEffect, useRef, useCallback } from 'react'
import { workflowsApi } from '@/lib/api'

interface VariableOption {
  field: string
  label: string
  token: string
  namespace: string
}

interface VariableAutocompleteProps {
  textareaRef: React.RefObject<HTMLTextAreaElement | HTMLInputElement | null>
  value: string
  onChange: (value: string) => void
}

export default function VariableAutocomplete({ textareaRef, value, onChange }: VariableAutocompleteProps) {
  const [allVars, setAllVars] = useState<VariableOption[]>([])
  const [show, setShow] = useState(false)
  const [filter, setFilter] = useState('')
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [triggerPos, setTriggerPos] = useState<number | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    workflowsApi.getTemplateVariables().then(res => {
      const flat: VariableOption[] = []
      for (const [ns, vars] of Object.entries(res.data)) {
        for (const v of vars) {
          flat.push({ ...v, namespace: ns })
        }
      }
      setAllVars(flat)
    }).catch(() => {})
  }, [])

  const filtered = allVars.filter(v =>
    v.token.toLowerCase().includes(filter.toLowerCase()) ||
    v.label.toLowerCase().includes(filter.toLowerCase())
  )

  const insertVariable = useCallback((token: string) => {
    if (triggerPos === null) return
    const el = textareaRef.current
    if (!el) return
    const before = value.substring(0, triggerPos)
    const after = value.substring(el.selectionStart || triggerPos)
    const newVal = before + token + after
    onChange(newVal)
    setShow(false)
    setFilter('')
    setTriggerPos(null)
    // Set cursor after inserted token
    setTimeout(() => {
      const pos = before.length + token.length
      el.setSelectionRange(pos, pos)
      el.focus()
    }, 0)
  }, [triggerPos, value, onChange, textareaRef])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!show) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIdx(i => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIdx(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (filtered.length > 0) {
        e.preventDefault()
        insertVariable(filtered[selectedIdx]?.token || filtered[0].token)
      }
    } else if (e.key === 'Escape') {
      setShow(false)
      setFilter('')
      setTriggerPos(null)
    }
  }, [show, filtered, selectedIdx, insertVariable])

  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.addEventListener('keydown', handleKeyDown as EventListener)
    return () => el.removeEventListener('keydown', handleKeyDown as EventListener)
  }, [textareaRef, handleKeyDown])

  // Watch for {{ trigger
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    const cursorPos = el.selectionStart || 0
    const textBefore = value.substring(0, cursorPos)
    const triggerMatch = textBefore.match(/\{\{([^}]*)$/)
    if (triggerMatch) {
      if (!show) {
        setTriggerPos(cursorPos - triggerMatch[0].length)
        setShow(true)
      }
      setFilter(triggerMatch[1])
      setSelectedIdx(0)
    } else if (show) {
      setShow(false)
      setFilter('')
      setTriggerPos(null)
    }
  }, [value, show])

  if (!show || filtered.length === 0) return null

  return (
    <div
      ref={dropdownRef}
      className="absolute z-50 mt-1 w-64 max-h-48 overflow-y-auto bg-card border border-border/60 rounded-lg shadow-lg"
    >
      {filtered.map((v, i) => (
        <button
          key={v.token}
          type="button"
          onMouseDown={(e) => { e.preventDefault(); insertVariable(v.token) }}
          className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between transition-all duration-150 ${
            i === selectedIdx
              ? 'bg-primary/10 text-primary'
              : 'text-foreground hover:bg-muted/50'
          }`}
        >
          <span>{v.label}</span>
          <span className="text-[10px] text-muted-foreground font-mono ml-2">{v.namespace}</span>
        </button>
      ))}
    </div>
  )
}
